/**
 * Scroll-driven animations — progress on a node's animation track is bound
 * to the page's scroll position instead of frame dt.
 *
 * Wire-up:
 *   - A scene sets `node.animation.scrollTrack = true` on any node whose
 *     animation should follow scroll.
 *   - `tickScroll(engine, scene, dt)` is called from main.ts per frame. It
 *     reads `window.scrollY` (or a custom scroll source), computes normalized
 *     progress in [0, 1], and sets `elapsedInIteration` so tickAnimation's
 *     downstream code samples the right keyframe.
 *   - The `scrollRange` field on the track lets you say "this animation
 *     triggers between 100px and 600px of scroll". Default range is
 *     [0, docHeight - viewportHeight] (full document scroll).
 *
 * Momentum/inertia: we track scroll velocity (delta-Y per frame), smooth it
 * with an exponential decay, and add a `momentum` offset to the raw scroll
 * progress. When the user releases, the smoothed velocity decays and the
 * momentum offset fades — net effect is the animation "carries" past the
 * user's release point and gently settles. This is the "video-like motion"
 * the brief calls out.
 *
 * The momentum is purely visual — it doesn't actually scroll the page; it
 * just adds an offset to the progress calculation. That keeps the page
 * itself stable and avoids fighting the browser's native scroll inertia.
 */

import type { Scene } from "./sceneGraph";

/** Last measured scroll velocity in px/s, smoothed. Positive = scrolling down. */
let smoothedVelocity = 0;
/** Last raw scrollY for velocity measurement. */
let lastScrollY = 0;
/** Time (seconds) of last scroll measurement. */
let lastScrollT = 0;
/**
 * Momentum offset added on top of raw scroll progress. Decays toward 0
 * exponentially. Measured in "fraction of scroll range".
 */
let momentum = 0;

/**
 * Half-life (seconds) of velocity smoothing and momentum decay. 0.18s feels
 * like a heavy physical scroller — momentum carries ~30% of the way before
 * fading.
 */
const DECAY_TAU = 0.18;

export interface ScrollState {
  /** Current scroll progress across the configured range, [0, 1]. */
  progress: number;
  /** Smoothed velocity in px/s. */
  velocity: number;
}

/**
 * Call once per frame. Reads window.scrollY, updates the smoothed velocity,
 * applies momentum, and writes elapsedInIteration on every scroll-tracked
 * animation in the scene.
 *
 * Returns the current scroll state so callers can display it in the HUD.
 */
export function tickScroll(
  scene: Scene,
  t: number,
  dt: number,
): ScrollState {
  const win = typeof window !== "undefined" ? window : null;
  const scrollY = win?.scrollY ?? 0;

  // Measure velocity in px/s using a finite difference over dt.
  if (dt > 0 && lastScrollT > 0) {
    const instantV = (scrollY - lastScrollY) / dt;
    // Exponential moving average. Coefficient `alpha = 1 - exp(-dt / tau)`
    // is frame-rate independent.
    const alpha = 1 - Math.exp(-dt / DECAY_TAU);
    smoothedVelocity = smoothedVelocity + (instantV - smoothedVelocity) * alpha;
  }
  lastScrollY = scrollY;
  lastScrollT = t;

  // Drive momentum from the smoothed velocity. While the user is scrolling
  // we just match the velocity; once they release, velocity decays and
  // momentum overshoots before settling. Cap so a fast flick doesn't shoot
  // the animation past the visible range.
  const maxOffset = 0.4;
  if (Math.abs(smoothedVelocity) > 1) {
    // Replenish momentum from current velocity.
    momentum = Math.max(-maxOffset, Math.min(maxOffset, smoothedVelocity * 0.001));
  } else {
    // Decay toward 0.
    const decay = Math.exp(-dt / DECAY_TAU);
    momentum *= decay;
  }

  // Default scroll range: full document scroll.
  const docH = (win?.document.documentElement.scrollHeight ?? 0) - (win?.innerHeight ?? 0);
  const defaultStart = 0;
  const defaultEnd = Math.max(1, docH);

  // Walk the scene's nodes and update scroll-tracked animations.
  for (const [, node] of scene.nodes) {
    if (!node.animation || !node.animation.scrollTrack) continue;
    const track = node.animation;
    const [start, end] = track.scrollRange ?? [defaultStart, defaultEnd];
    const range = end - start;
    if (range <= 0) continue;
    const rawProgress = (scrollY - start) / range;
    const totalProgress = rawProgress + momentum;
    const clamped = Math.max(0, Math.min(1, totalProgress));
    // Set the time-driven fields directly. tickAnimation (called next from
    // main.ts's per-node update loop) will see the elapsedInIteration we
    // wrote here and sample the right keyframe. We bypass tickAnimation's
    // own dt accumulator by zeroing its duration math — see note below.
    track.elapsedInIteration = clamped * track.duration;
    track.currentIteration = Math.floor(clamped * iterationsNumber(track));
  }

  return {
    progress: docH > 0 ? scrollY / docH : 0,
    velocity: smoothedVelocity,
  };
}

/**
 * Numeric form of KeyframeTrack.iterations: returns Infinity for "infinite".
 * Used by scroll-driven progress so we can multiply progress by it directly.
 */
export function iterationsNumber(track: { iterations: number | "infinite" }): number {
  return track.iterations === "infinite" ? Infinity : track.iterations;
}

/** Reset state — useful when switching scenes. */
export function resetScrollState(): void {
  smoothedVelocity = 0;
  momentum = 0;
  lastScrollY = 0;
  lastScrollT = 0;
}